// Country-aware remittance (bank) details printed on documents — Feature 1.3 (international
// remittance). The SA "branch code" is not universal: Americans use a routing/ABA number, the
// British a sort code, most of Europe an IBAN + BIC. This module is the ONE source of truth for
// (a) which remittance inputs to show in Settings for a company's country, and (b) which rows to
// print on an invoice — for the on-screen document (document_detail.js) AND the public/shared
// document (public_doc.js). The API mirrors this ordering in Python (branding_service.remittance_rows)
// for the server-rendered PDF; if you change the order or labels here, change it there too.
//
// SWIFT/BIC + IBAN are offered for EVERY country (international payments), and a blank field never
// prints a row. Keys are the canonical organisation.company columns (see migration 118).

// Canonical column keys + their display labels + a concrete placeholder + whether the value is a
// code/number best shown monospace. Labels are kept ASCII/simple so the Python PDF mirror matches.
const FIELD = {
  bank_name:           { label: 'Bank',          placeholder: 'e.g. First National Bank', mono: false },
  bank_account_name:   { label: 'Account name',  placeholder: 'e.g. Riverside Trading (Pty) Ltd', mono: false },
  bank_account_no:     { label: 'Account no',    placeholder: 'e.g. 62011112222', mono: true },
  bank_branch_code:    { label: 'Branch code',   placeholder: 'e.g. 250655', mono: true },
  bank_routing_number: { label: 'Routing / ABA', placeholder: 'e.g. 021000021', mono: true },
  bank_sort_code:      { label: 'Sort code',     placeholder: 'e.g. 20-00-00', mono: true },
  bank_swift:          { label: 'SWIFT / BIC',   placeholder: 'e.g. FIRNZAJJ', mono: true },
  bank_iban:           { label: 'IBAN',          placeholder: 'e.g. NL91 ABNA 0417 1643 00', mono: true },
};

// Every canonical key — callers (settings signals) and the API (_REMIT_FIELDS) stay in sync with this.
export const REMIT_KEYS = Object.keys(FIELD);

// SWIFT + IBAN close every set (international payments), so the per-country lists below only name
// the LOCAL identifiers; _order() appends the international pair.
const INTL = ['bank_swift', 'bank_iban'];

// EU / SEPA countries — IBAN + BIC is the norm; no branch/sort/routing. (The countries the app
// seeds today are ZA, NL, ES; the rest are here so the field set is already correct if a company is
// created in another SEPA country.)
const EU = new Set(['NL', 'ES', 'DE', 'FR', 'IT', 'IE', 'BE', 'AT', 'PT', 'FI', 'LU', 'GR',
  'SK', 'SI', 'LT', 'LV', 'EE', 'CY', 'MT', 'NL', 'HR']);

// Per-country LOCAL identifier order (bank + account holder always first). SWIFT/IBAN appended.
const LOCAL = {
  ZA: ['bank_name', 'bank_account_name', 'bank_account_no', 'bank_branch_code'],
  US: ['bank_name', 'bank_account_name', 'bank_account_no', 'bank_routing_number'],
  GB: ['bank_name', 'bank_account_name', 'bank_account_no', 'bank_sort_code'],
  // EU handled below (IBAN/BIC only, no local account no)
};
const EU_LOCAL = ['bank_name', 'bank_account_name'];
const DEFAULT_LOCAL = ['bank_name', 'bank_account_name', 'bank_account_no'];

function _order(iso2){
  const cc = String(iso2 || '').trim().toUpperCase();
  let local;
  if(LOCAL[cc]) local = LOCAL[cc];
  else if(EU.has(cc)) local = EU_LOCAL;
  else local = DEFAULT_LOCAL;
  // GB conventionally quotes IBAN before SWIFT for international; everyone else SWIFT then IBAN is
  // fine. Keep it simple: append the international pair (IBAN first for GB/EU, SWIFT first otherwise).
  const intl = (cc === 'GB' || EU.has(cc)) ? ['bank_iban', 'bank_swift'] : INTL;
  return [...local, ...intl];
}

/**
 * remittanceFields(iso2) → [{ key, label, placeholder, mono }] — the inputs to render in Settings
 * for a company in this country, in order. SWIFT + IBAN are always included.
 */
export function remittanceFields(iso2){
  return _order(iso2).map(key => ({ key, ...FIELD[key] }));
}

/**
 * remittanceRows({ remittance, iso2 }) → [{ label, value, mono }] — the NON-BLANK rows to print on
 * the document, in the country's order. An empty remittance yields []. A field that is not part of
 * the country's set (e.g. a stray branch code on an EU company) is never printed.
 */
export function remittanceRows({ remittance, iso2 } = {}){
  const m = remittance || {};
  const rows = [];
  for(const key of _order(iso2)){
    const raw = m[key];
    const value = (raw == null ? '' : String(raw)).trim();
    if(!value) continue;
    rows.push({ key, label: FIELD[key].label, value, mono: FIELD[key].mono });
  }
  return rows;
}
